import { PencilIcon } from '@heroicons/react/24/outline';
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { Button } from '../../components/forms/Buttons/Button';
import Select from '../../components/forms/Select/Select';
import CustomDialog from '../../components/modals/CustomDialog/CustomDialog';
import Table from '../../components/Table/Table';
import { getDistricts, getSchools } from '../../services/catalog/catalog';
import { deleteDistrict } from '../../services/catalog/districts';
import { UserContext } from '../../utils/contexts/UserContext';
import EditDistrict from './EditDistrict';

export default function Districts() {
  const { user } = useContext(UserContext);
  const abortControllerRef = useRef(null);

  const [districtToEdit, setDistrictToEdit] = useState(null);
  const [districtToDelete, setDistrictToDelete] = useState(null);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [districts, setDistricts] = useState([]);
  const [page, setPage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [pageSize] = useState(50);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isExamCenter, setIsExamCenter] = useState(null);
  const [addressIsNull, setAddressIsNull] = useState(null);
  const isSchoolAdmin = user?.roles?.some((role) => role?.code === 'VP');
  const isSuperSuperAdmin = user?.roles?.some((role) => role?.code === 'SSA');
  const [schools, setSchools] = useState([]);
  const [school, setSchool] = useState(null);
  const [schoolsLoading, setSchoolsLoading] = useState(false);
  // const isSchoolAdmin = true;

  const createController = () => {
    abortControllerRef.current?.abort();
    const currentController = new AbortController();
    abortControllerRef.current = currentController;
    return currentController;
  };

  const fetchSchools = useCallback(() => {
    setSchoolsLoading(true);
    getSchools({ page_size: 50 }).then((res) => {
      const options = res.results.map((school) => ({
        label: school.name,
        value: school.uuid,
      }));
      setSchools(options.sort((a, b) => a.label.localeCompare(b.label)));
    }).finally(() => {
      setSchoolsLoading(false);
    });
  }, [setSchools, setSchoolsLoading]);

  const fetchDistricts = useCallback((page, signal) => {
    setLoading(true);
    getDistricts({page_size: pageSize, page: page, is_exam_center: isExamCenter, address_isnull: addressIsNull, school: school}, signal)
      .then((res) => {
        setDistricts(res.results);
        setCount(res.count);
        setPageCount(Math.ceil(res.count / pageSize));
      })
      .catch((err) => {
        console.log(err);

      })
      .finally(() => {
        if (!signal?.aborted) {
        setLoading(false);
      }
      });
  }, [pageSize, isExamCenter, addressIsNull, school]);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  useEffect(() => {
    fetchSchools();
  }, [fetchSchools]);

  
  useEffect(() => {
    const currentController = createController();
    setPage(1);
    fetchDistricts(1, currentController?.signal);
    return () => {
      currentController?.abort();
    };
  }, [isExamCenter, addressIsNull, fetchDistricts, pageSize, page]);



  const columns = React.useMemo(
    () => [
      {
        Header: 'Район ID',
        accessor: 'district_id',
      },
      {
        Header: 'Название района',
        accessor: 'name',
      },
      ...(!isSchoolAdmin ? [{
        Header: 'Школа',
        accessor: 'school.name',
      },] : []),
      {
        Header: 'Экзаменационный центр',
        accessor: 'is_exam_center',
        Cell: (props) => (
          <div className='text-center py-1'>
            {props.value ? (
              <span className='rounded-md bg-green-600 px-2 py-1 text-white'>
                Да
              </span>
            ) : (
              <span className='rounded-md bg-red-600 px-2 py-1 text-white'>
                Нет
              </span>
            )}
          </div>
        ),
      },
      {
        Header: 'Адрес',
        accessor: 'address',
      },
      {
        Header: 'Географическое место',
        accessor: 'is_geographical',
        Cell: (props) => (
          <div className='text-center'>
            {props.value ? (
              <span className=' rounded-md bg-blue-200 px-2 py-1'>Да</span>
            ) : (
              <span>Нет</span>
            )}
          </div>
        ),
      },
      {
        Header: 'Экзам центр заполнен',
        accessor: 'is_full',
        Cell: (props) =>
          props?.row?.original?.is_exam_center && (
            <div className='text-center'>
              {props.value ? (
                <span className='p2-1 p1-2 rounded-md border-2 border-red-600 px-2 py-1 font-bold text-red-600'>
                  Да
                </span>
              ) : (
                <span className='rounded-md border-2 border-green-600 px-2 py-1 font-bold text-green-600'>
                  Нет
                </span>
              )}
            </div>
          ),
      },
      {
        Header: '',
        accessor: 'actions',
        fixed: 'right',
        Cell: (props) => (
          <div className='flex gap-2'>
            {(isSchoolAdmin || isSuperSuperAdmin) && (
              <Button
              Icon={PencilIcon}
              onClick={() => {
                setEditModalOpen(true);
                setDistrictToEdit(props.row.original);
              }}
            />)}
            {/* <Button
              Icon={TrashIcon}
              color='error'
              onClick={() => {
                setDeleteModalOpen(true);
                setDistrictToDelete(props.row.original);
              }}
            /> */}
          </div>
        ),
      },
    ],
    [],
  );

  const nextPage = () => {
    const currentController = createController();
    const newPage = page + 1;
    setPage(newPage);
    fetchDistricts(newPage, currentController?.signal);
  };

  const previousPage = () => {
    const currentController = createController();
    const newPage = page - 1;
    setPage(newPage);
    fetchDistricts(newPage, currentController?.signal);
  };

  const gotoPage = (pageNumber) => {
    const currentController = createController();
    setPage(pageNumber);
    fetchDistricts(pageNumber, currentController?.signal);
  };

  return (
    <div>
      <div className='mb-4 flex items-center justify-between gap-4'>
        <div className='flex gap-4'>
       {!isSchoolAdmin && ( <Select
            label='Школа:'
            className='w-56'
            value={
              school
            }
            isSearchable={true}
            isClearable={true}
            isDisabled={schoolsLoading}
            isLoading={schoolsLoading}
            options={schools}
            onChange={(e) => {
              setSchool(e?.value);
            }}
          />)}
          <Select
            label='Экзам центр:'
            className='w-56'
            value={
              isExamCenter
            }
            isSearchable={false}
            options={[
              {
                label: 'Все районы',
                value: null,
              },
              {
                label: 'Экзам центр',
                value: true,
              },
              {
                label: 'Не экзам центр',
                value: false,
              },
            ]}
            onChange={(e) => {
              setIsExamCenter(e.value);
              setPage(1);
            }}
          />
          <Select
            className='w-56'
            value={addressIsNull}
            label='Адрес:'
            isSearchable={false}
            options={[
              {
                label: 'Все районы',
                value: null,
              },
              {
                label: 'Без адреса',
                value: true,
              },
              {
                label: 'С адресом',
                value: false,
              },
            ]}
            placeholder='Фильтр: Адрес'
            onChange={(e) => {
              setAddressIsNull(e.value);
              setPage(1);
            }}
          />
        </div>
        {(isSchoolAdmin || isSuperSuperAdmin)  && (        <Button
          text='Добавить'
          onClick={() => {
            setEditModalOpen(true);
            setDistrictToEdit(null);
          }}
        />)}
      </div>

      <Table
        columns={columns}
        data={districts}
        count={count}
        pageNumber={page}
        nextPage={nextPage}
        previousPage={previousPage}
        gotoPage={gotoPage}
        loading={loading}
        pageCount={pageCount}
      />
      {false && (
        <CustomDialog
          isOpen={deleteModalOpen}
          setIsOpen={setDeleteModalOpen}
          closeModal={() => {
            setDeleteModalOpen(false);
          }}
          headerText={'Удалить'}
        >
          <div className='flex flex-col gap-4'>
            <p>
              Вы уверены, что хотите удалить ученика{' '}
              <b>{districtToDelete?.name}</b>?
            </p>
            <Button
              type='button'
              color='error'
              text='Удалить'
              onClick={() => {
                deleteDistrict(districtToDelete?.uuid).then(() => {
                  getDistricts()
                    .then((res) => {
                      setDistricts(res.results);
                    })
                    .finally(() => {
                      setDeleteModalOpen(false);
                    });
                });
              }}
            />
          </div>
        </CustomDialog>
      )}
      <EditDistrict
        user={user}
        editModalOpen={editModalOpen}
        setEditModalOpen={setEditModalOpen}
        district={districtToEdit}
        schools={schools}
        isSuperSuperAdmin={isSuperSuperAdmin}
        setDistricts={setDistricts}
        onSuccess={() => {
          const currentController = createController();
          fetchDistricts(page, currentController?.signal);
          
        }}
      />
    </div>
  );
}
